/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Page } from '../components/console/ui';
import { PlusIc } from '../components/console/icons';
import ProjectsHeader from '../components/projects/ProjectsHeader';
import ProjectGrid from '../components/projects/ProjectGrid';
import ProjectEmptyState from '../components/projects/ProjectEmptyState';
import ProjectLoadingState from '../components/projects/ProjectLoadingState';
import ProjectModal from '../components/projects/ProjectModal';
import DeleteProjectModal from '../components/projects/DeleteProjectModal';
import AuditLogTable from '../components/projects/AuditLogTable';
import { KYCRequiredModal } from '../components/kyc';
import { useProjects } from '../hooks/useProjects';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { useAuditLogs } from '../hooks/useAuditLogs';
import { useKYCStatus } from '../hooks/useKYCStatus';
import { isKYCEnforcementEnabled } from '../config/featureFlags';

interface Project {
  id: string;
  name: string;
  description?: string;
  created_at: string;
}

const RECENT_ACTIVITY_ROWS = 8;

const Projects: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [showKYCModal, setShowKYCModal] = useState(false);

  const { isKYCVerified } = useKYCStatus();
  const { selectedProjectId, setSelectedProject } = useSelectedProject();

  const {
    projects,
    isLoading,
    createProject,
    isCreating,
    updateProject,
    isUpdating,
    deleteProject,
    isDeleting,
  } = useProjects();

  const projectList = projects as Project[];
  const projectIds = useMemo(() => projectList.map((p) => p.id), [projectList]);
  const projectNames = useMemo(
    () => Object.fromEntries(projectList.map((p) => [p.id, p.name])) as Record<string, string>,
    [projectList],
  );

  // Projects whose names only differ by case ("Demo" vs "demo") are hard to tell apart in pickers.
  const duplicateNames = useMemo(() => {
    const groups = new Map<string, Project[]>();
    projectList.forEach((p) => {
      const key = p.name.trim().toLowerCase();
      groups.set(key, [...(groups.get(key) || []), p]);
    });
    return Array.from(groups.values()).find((g) => g.length > 1) || null;
  }, [projectList]);

  const { logs: recentLogs, isLoading: logsLoading } = useAuditLogs({
    projectIds,
    pageSize: RECENT_ACTIVITY_ROWS,
    enabled: projectIds.length > 0,
  });

  const handleOpenCreateModal = () => {
    // Only check KYC if enforcement is enabled
    if (isKYCEnforcementEnabled() && !isKYCVerified) {
      setShowKYCModal(true);
      return;
    }
    setEditingProject(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (project: Project) => {
    setEditingProject(project);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
  };

  const handleSubmit = (data: { name: string; description: string }) => {
    if (editingProject) {
      updateProject({ id: editingProject.id, data }, { onSuccess: handleCloseModal });
    } else {
      createProject(data, { onSuccess: handleCloseModal });
    }
  };

  const handleDelete = (project: Project) => {
    setProjectToDelete(project);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (projectToDelete) {
      deleteProject(projectToDelete.id, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setProjectToDelete(null);
        },
      });
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setProjectToDelete(null);
  };

  return (
    <Page>
      <ProjectsHeader
        subtitle="Group pods, storage and keys by team or workload. The project picker in the top bar filters every page."
        projectCount={projectList.length}
        actions={
          <Button onClick={handleOpenCreateModal}>
            <PlusIc className="w-4 h-4" />
            New project
          </Button>
        }
      />

      {duplicateNames && (
        <div className="px-4 py-3 rounded-[10px] bg-ps-warn-bg text-ps-warn text-sm flex flex-wrap justify-between items-center gap-3">
          <span>
            {duplicateNames.length === 2
              ? `Two projects are named “${duplicateNames[0].name}” and “${duplicateNames[1].name}”.`
              : `${duplicateNames.length} projects share the name “${duplicateNames[0].name}”.`}{' '}
            Rename one so they’re easy to tell apart in pickers.
          </span>
          <Button variant="secondary" size="sm" onClick={() => handleOpenEditModal(duplicateNames[duplicateNames.length - 1])}>
            Rename
          </Button>
        </div>
      )}

      {isLoading ? (
        <ProjectLoadingState />
      ) : projectList.length > 0 ? (
        <>
          <ProjectGrid
            projects={projectList}
            currentProjectId={selectedProjectId}
            onSwitch={(p) => setSelectedProject(p.id)}
            onEdit={handleOpenEditModal}
            onDelete={handleDelete}
          />
          <p className="-mt-2 m-0 text-ps-subtle text-[13px]">
            Cards show each project’s own resources - not the wallet balance, which is account-wide.
          </p>
        </>
      ) : (
        <ProjectEmptyState onCreateProject={handleOpenCreateModal} />
      )}

      {projectList.length > 0 && (
        <section className="flex flex-col gap-3" aria-labelledby="recent-activity">
          <div className="flex flex-wrap justify-between items-baseline gap-3">
            <h2 id="recent-activity" className="m-0 text-lg font-medium text-ps-text">
              Audit log
            </h2>
            <span className="text-ps-subtle text-[13px]">
              All projects · latest activity ·{' '}
              <Link to="/projects/audit-log" className="ps-link">
                Filter and export
              </Link>
            </span>
          </div>
          <AuditLogTable
            logs={recentLogs.slice(0, RECENT_ACTIVITY_ROWS)}
            projectNames={projectNames}
            emptyMessage={
              logsLoading
                ? 'Loading activity…'
                : 'No activity recorded yet. Creating, starting or deleting resources will show up here.'
            }
          />
        </section>
      )}

      <ProjectModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        editingProject={editingProject}
        isSubmitting={isCreating || isUpdating}
      />

      <DeleteProjectModal
        isOpen={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        projectName={projectToDelete?.name || ''}
        isDeleting={isDeleting}
      />

      <KYCRequiredModal isOpen={showKYCModal} onClose={() => setShowKYCModal(false)} />
    </Page>
  );
};

export default Projects;
